import { useEffect, useState } from 'react';
import type { AgentRole } from '@aiai/shared';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLoop, fetchLoop, fetchProject, fetchVersion, updateLoop } from '../api';
import { useCanonicalProjectRef } from '../hooks/useCanonicalProjectRef';
import { qk } from '../api/queryKeys';
import { errorMessage } from '../lib/api';
import { loopsHref, projectNav } from '../lib/projectNav';
import { useWorkspacePath } from '../hooks/useWorkspacePath';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Callout } from '../components/Callout';
import { FormField } from '../components/FormField';
import { LoopHealthPanel } from '../components/LoopHealthPanel';
import { PageHeader } from '../components/PageHeader';
import { Spinner } from '../components/Spinner';
import { ErrorState, LoadingState } from '../components/States';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { CRON_PRESETS, cronLabel } from '../lib/cronPresets';
import { AGENT_TEMPLATES, type AgentTemplate } from '../lib/loopTemplates';

/** The two agent roles. Worker writes code (→ PR); Planner is read-only (files
 *  issues, never touches code). This is the honest primitive behind every loop. */
type Role = 'worker' | 'planner';

function TemplateGallery({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (template: AgentTemplate) => void;
}) {
  return (
    <div className="space-y-3">
      <h2 className="text-sm font-semibold text-ink">Start from a template</h2>
      <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {AGENT_TEMPLATES.map((t) => {
          const selected = selectedId === t.id;
          return (
            <li key={t.id}>
              <div
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => onSelect(t)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(t)}
                className={`card flex cursor-pointer flex-col gap-2 p-3.5 transition-colors ${
                  selected
                    ? 'border-signal/60 bg-signal-soft'
                    : 'hover:border-line-strong hover:bg-surface-2'
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-ink">{t.name}</p>
                    <p className="mt-0.5 text-xs text-ink-muted">{t.description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-[0.6rem] text-ink-faint">
                    {cronLabel(t.cronExpression)}
                  </span>
                  <button
                    type="button"
                    aria-label="Use template"
                    tabIndex={-1}
                    className={`btn-sm ${selected ? 'btn-primary' : 'btn-secondary'}`}
                  >
                    {selected ? 'Selected' : 'Use template'}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

type Mode = 'create' | 'edit';

/**
 * A loop is a scheduled worker (an "agent-runtime"). It clocks in on the
 * schedule, reads its mission + the project's issue board + the repo, and does
 * the job — fixing code (→ a PR), or creating / managing issues. The whole
 * definition is a free-text mission + a schedule.
 */
export function LoopEditorPage({ mode }: { mode: Mode }) {
  const navigate = useNavigate();
  const { wp } = useWorkspacePath();
  const queryClient = useQueryClient();
  const params = useParams();
  const projectId = params.projectId ?? '';
  const loopId = params.loopId ?? '';
  const loopQuery = useQuery({
    queryKey: qk.loop(loopId),
    queryFn: () => fetchLoop(loopId),
    enabled: mode === 'edit' && !!loopId,
  });

  // Fetch the project code-first: the route param is already the code, and in
  // edit mode the loop carries its projectCode — only fall back to the UUID.
  const effectiveProjectId =
    mode === 'edit'
      ? (loopQuery.data?.projectCode ?? loopQuery.data?.projectId ?? projectId)
      : projectId;

  const projectQuery = useQuery({
    queryKey: qk.project(effectiveProjectId),
    queryFn: () => fetchProject(effectiveProjectId),
    enabled: !!effectiveProjectId,
  });

  // Keep the URL on the pretty code; use it for the Loops back-links too.
  useCanonicalProjectRef(projectId, projectQuery.data?.code);
  const projectRef = projectQuery.data?.code ?? effectiveProjectId;

  const versionQuery = useQuery({
    queryKey: qk.version,
    queryFn: fetchVersion,
    staleTime: 60_000,
  });

  useDocumentTitle(
    mode === 'create'
      ? 'New loop'
      : loopQuery.data
        ? `Edit · ${loopQuery.data.name}`
        : 'Edit loop',
  );

  // ── Form state ─────────────────────────────────────────────────────────────
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [cronExpression, setCronExpression] = useState('0 0 * * 1');
  const [presetKey, setPresetKey] = useState('0 0 * * 1');
  const [enabled, setEnabled] = useState(true);
  const [role, setRole] = useState<Role>('worker');
  // Engineer mode: build the ready backlog instead of running a mission. Only
  // valid for the worker role (a planner has nothing to build).
  const [drain, setDrain] = useState(false);
  const [seeded, setSeeded] = useState(mode === 'create');
  const [formError, setFormError] = useState<string | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  const isDrain = role === 'worker' && drain;
  const pipeline: AgentRole[] = [role];
  const loopMode = isDrain ? 'drain' : 'mission';

  useEffect(() => {
    if (mode === 'edit' && loopQuery.data && !seeded) {
      const loop = loopQuery.data;
      setName(loop.name);
      setDescription(loop.description);
      setPrompt(loop.prompt);
      setCronExpression(loop.cronExpression);
      setPresetKey(
        CRON_PRESETS.some(p => p.value !== '' && p.value === loop.cronExpression)
          ? loop.cronExpression
          : '',
      );
      setEnabled(loop.enabled);
      setRole(loop.pipeline?.includes('planner') ? 'planner' : 'worker');
      setDrain(loop.mode === 'drain');
      setSeeded(true);
    }
  }, [mode, loopQuery.data, seeded]);

  /** Switching to the planner role clears Engineer/drain — a planner never builds. */
  function handleRoleChange(next: Role) {
    setRole(next);
    if (next === 'planner') setDrain(false);
  }

  // ── Mutations ──────────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: () =>
      createLoop({
        projectId: effectiveProjectId,
        name,
        description: description || undefined,
        prompt: isDrain ? '' : prompt,
        cronExpression,
        enabled,
        pipeline,
        mode: loopMode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.projectLoops(effectiveProjectId) });
      navigate(wp(loopsHref(projectQuery.data?.kind ?? 'repo', projectRef)));
    },
    onError: (err) => setFormError(errorMessage(err, 'Failed to create loop')),
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateLoop(loopId, {
        name,
        description: description || undefined,
        prompt: isDrain ? '' : prompt,
        cronExpression,
        enabled,
        pipeline,
        mode: loopMode,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.projectLoops(effectiveProjectId) });
      queryClient.invalidateQueries({ queryKey: qk.loop(loopId) });
      navigate(wp(loopsHref(projectQuery.data?.kind ?? 'repo', projectRef)));
    },
    onError: (err) => setFormError(errorMessage(err, 'Failed to update loop')),
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }
    if (!isDrain && !prompt.trim()) {
      setFormError('Mission is required');
      return;
    }
    if (!cronExpression.trim()) {
      setFormError('Cron expression is required');
      return;
    }
    if (mode === 'create') {
      createMutation.mutate();
    } else {
      updateMutation.mutate();
    }
  }

  function handlePresetChange(value: string) {
    setPresetKey(value);
    if (value) setCronExpression(value);
  }

  function applyTemplate(template: AgentTemplate) {
    setSelectedTemplateId(template.id);
    setName(template.name);
    setDescription(template.description);
    setPrompt(template.prompt);
    setRole((template.role as Role) ?? 'worker');
    // Every template is a mission; Engineer/drain is a manual editor toggle only.
    setDrain(false);
    setCronExpression(template.cronExpression);
    setPresetKey(
      CRON_PRESETS.some(p => p.value !== '' && p.value === template.cronExpression)
        ? template.cronExpression
        : '',
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  if (mode === 'edit' && loopQuery.isLoading) {
    return <LoadingState label="Loading loop…" />;
  }
  if (mode === 'edit' && loopQuery.isError) {
    return (
      <ErrorState
        message={errorMessage(loopQuery.error, 'Failed to load loop')}
        onRetry={() => loopQuery.refetch()}
      />
    );
  }

  const project = projectQuery.data;
  const nav = projectNav(project);
  const breadcrumbs = [
    { label: nav.rootLabel, to: wp(nav.rootTo) },
    ...(project ? [{ label: nav.name, to: wp(nav.selfTo) }] : []),
    { label: 'Loops', to: wp(loopsHref(project?.kind ?? 'repo', projectRef)) },
    { label: mode === 'create' ? 'New loop' : 'Edit loop' },
  ];

  return (
    <div className="space-y-6">
      <Breadcrumbs items={breadcrumbs} />

      <PageHeader
        title={mode === 'create' ? 'New scheduled loop' : 'Edit loop'}
        subtitle="A loop is an agent that clocks in on a schedule. It reads its mission, your issue board, and the repo, then decides what to do — fix code and open a PR, or create and manage issues. Just describe who it is."
      />

      {mode === 'create' && (
        <TemplateGallery
          selectedId={selectedTemplateId}
          onSelect={applyTemplate}
        />
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="card space-y-5 p-5">
          <FormField label="Name">
            <input
              type="text"
              className="input w-full"
              placeholder="Prio-1 bug fixer"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isPending}
            />
          </FormField>

          <FormField label="Description">
            <input
              type="text"
              className="input w-full"
              placeholder="Brief summary of this agent"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isPending}
            />
          </FormField>

          <FormField
            label="Role"
            hint={
              role === 'planner'
                ? 'Planner: a read-only analyst. It investigates and files/closes issues but has no tools to edit code, run commands, or open a PR (e.g. Product Manager, security researcher).'
                : 'Worker: the full toolkit. It carries out its mission and can change code and open a pull request (e.g. bug fixer, dependency bumper, the Engineer).'
            }
          >
            <select
              className="input w-full"
              value={role}
              onChange={(e) => handleRoleChange(e.target.value as Role)}
              disabled={isPending}
            >
              <option value="worker">Worker — writes code (→ PR)</option>
              <option value="planner">Planner — read-only, never writes code</option>
            </select>
          </FormField>

          {role === 'worker' && (
            <div className="flex items-start gap-3">
              <input
                id="loop-drain"
                type="checkbox"
                className="checkbox mt-0.5"
                checked={drain}
                onChange={(e) => setDrain(e.target.checked)}
                disabled={isPending}
              />
              <label htmlFor="loop-drain" className="text-sm text-ink-muted">
                <span className="text-ink">Engineer mode</span> — build the ready
                backlog until it's empty, instead of running a mission. No mission
                needed; pair it with a Product Manager loop scheduled a little earlier.
              </label>
            </div>
          )}

          <FormField label="Schedule">
            <select
              className="input w-full"
              value={presetKey}
              onChange={(e) => handlePresetChange(e.target.value)}
              disabled={isPending}
            >
              {CRON_PRESETS.map((p) => (
                <option key={p.label} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </FormField>

          <FormField
            label="Cron expression"
            hint={`Standard 5-field cron expression, evaluated in the platform timezone (${versionQuery.data?.timezone ?? 'Europe/Amsterdam'}). Fields: minute hour day month weekday.`}
          >
            <input
              type="text"
              className="input w-full font-mono text-sm"
              placeholder="e.g. 0 */4 * * * (every 4 hours)"
              value={cronExpression}
              onChange={(e) => {
                setCronExpression(e.target.value);
                setPresetKey('');
              }}
              disabled={isPending}
            />
          </FormField>

          <div className="flex items-center gap-3">
            <input
              id="loop-enabled"
              type="checkbox"
              className="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              disabled={isPending}
            />
            <label htmlFor="loop-enabled" className="text-sm text-ink-muted">
              Enabled (fires automatically on schedule)
            </label>
          </div>

        </div>

        {/* Mission — not needed for a drain (Engineer) loop, which just builds the backlog. */}
        {isDrain ? (
          <div className="card p-5">
            <h2 className="text-sm font-semibold text-ink">No mission needed</h2>
            <p className="mt-1 text-xs text-ink-muted">
              An Engineer loop has no brief. When it fires it picks the oldest
              ready <span className="font-mono">implement</span> ticket and builds it,
              then continues through the backlog until nothing buildable is left.
              Pair it with a Product Manager loop scheduled a bit earlier so the
              board is filled before the Engineer starts.
            </p>
          </div>
        ) : (
          <div className="card space-y-3 p-5">
            <div>
              <h2 className="text-sm font-semibold text-ink">Mission</h2>
              <p className="mt-0.5 text-xs text-ink-muted">
                Who this agent is and what it should do — like onboarding a teammate.
                It figures out the rest from the board and the repo. Markdown supported.
              </p>
            </div>
            <textarea
              className="input min-h-48 w-full font-mono text-sm"
              placeholder={`You are a prio-1 engineer. Look at the issue board, pick the highest-priority open bug, fix it, and mark that issue done. If you find new bugs while working, file them.`}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              disabled={isPending}
            />
          </div>
        )}

        {formError && <Callout tone="danger">{formError}</Callout>}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(wp(loopsHref(projectQuery.data?.kind ?? 'repo', projectRef)))}
            disabled={isPending}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={isPending}>
            {isPending && <Spinner className="h-3.5 w-3.5" />}
            {mode === 'create' ? 'Create loop' : 'Save changes'}
          </button>
        </div>
      </form>

      {mode === 'edit' && loopId && (
        <div className="card p-5">
          <LoopHealthPanel loopId={loopId} />
        </div>
      )}
    </div>
  );
}
